import { requestGetPromotionProductsMoloco } from '@apis/renewal/promotion';
import useGlobalState from '@hooks/useGlobalState';
import useMyHeartItem from '@hooks/useMyHeartItem';
import useRenderProducts from '@hooks/useRenderProducts';
import { useWckQuery } from '@hooks/wckQuery/useWckQuery';
import { getMolocoCustNo } from '@wconcept/wconcept-common/utils/domainUtil';
import AdProductSection from '@wconcept/wconcept-ui-renewal/src/components/organisms/_section/AdProductSection';
import { getCookieWckDisplayMktSID } from '@wconcept/wconcept-common/utils/cookie';
import { uuidv4 } from '@wconcept/wconcept-common/utils/commonUtils';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useGa4Event from '@hooks/useGa4Event';
import { keepPreviousData } from '@tanstack/react-query';
import PrdItemSkeleton from '@wconcept/wconcept-ui-renewal/src/components/molecules/_skeleton/PrdItemSkeleton';
import {
  sendMolocoDecisionClickTracker,
  sendMolocoImpTracker,
} from '@tags/moloco';
import useMolocoLogger from '@hooks/useMolocoLogger';
import useBrazeLogger from '@hooks/useBrazeLogger';
import { useCustomAd } from '@components/common/LpFilterProductList/hooks';
import { PromotionInfo } from '@wconcept/wconcept-common/models/renewal/promotion';
import { type PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';

function AdvertisementModule({
  order,
  registerModule,
  promotion,
  contentId,
  promotionId,
  promotionName,
  title,
  bgType,
  visualTokens,
  isMobile,
}: {
  order: number;
  registerModule?: (contentId: string, element: HTMLElement | null) => void;
  promotion?: PromotionInfo;
  contentId?: string;
  promotionId?: number;
  promotionName?: string;
  title?: string;
  bgType?: string;
  visualTokens: PromotionModuleVisualTokens;
  isMobile: boolean;
}) {
  const { assistData } = useGlobalState();
  const { sendGa4AddToWishProduct, sendGa4SelectItem } = useGa4Event();
  // const { onClickHeart } = useMyHeartItem({
  //   isLogin: !!assistData?.isLogin,
  // });
  const itemsRef = useRef<HTMLDivElement[]>([]);
  const hasScrolled = useRef(false);
  const [isFirstVisible, setIsFirstVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(null);
  const isTouching = useRef(false);
  const { sendMolocoAddToWish } = useMolocoLogger();
  const { sendBrazeRenewalMyHeart } = useBrazeLogger();
  const isCustomAd = useCustomAd();
  const { appInfo } = useGlobalState();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    presetBgType,
    customColorsLite: customTwoFieldColors,
    productUnitColorType: advertisementProductColorType,
    productSkeletonBgType: advertisementSkeletonBgType,
  } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'twoField',
  });
  const sectionColorType = customTwoFieldColors ?? presetBgType;

  const { renderProducts } = useRenderProducts({
    doAfterAddMyHeart: (product, index) => {
      sendGa4AddToWishProduct({
        product,
        myheartYn: true,
        listNo: (index ?? 0) + 1,
        ecommercePath: '프로모션',
        page: 'promotion',
        area: 'promotion_ad_myheart',
        issueCd: promotionId?.toString(),
        issueNm: title,
        affiliation: 'moloco',
      });
      sendMolocoAddToWish({
        itemCd: product.itemCd,
        amount: product.finalPrice,
        pageId: window.location.pathname,
      });
      sendBrazeRenewalMyHeart({
        product,
        pageId: window.location.pathname,
        useGender: true,
        defaultGender: 'women',
      });
      // onClickHeart?.(product, true, index || 0);
    },
    doAfterDeleteMyHeart: (product, index) => {
      sendGa4AddToWishProduct({
        product,
        myheartYn: false,
        listNo: (index ?? 0) + 1,
        ecommercePath: '프로모션',
        page: 'promotion',
        area: 'promotion_ad_myheart',
        issueCd: promotionId?.toString(),
        issueNm: promotionName,
        affiliation: 'moloco',
      });
      // onClickHeart?.(product, false, index || 0);
    },
  });

  // Moloco 광고 상품 데이터 조회
  const {
    data: molocoProducts,
    isLoading,
    error,
  } = useWckQuery({
    queryKey: ['moloco-ad-products', promotionId, contentId],
    queryFn: async () => {
      if (!promotionId || !contentId) {
        return [];
      }

      const sessionId = (await getCookieWckDisplayMktSID()) as
        | string
        | undefined;
      const userId = (await getMolocoCustNo(false)) || '';
      const result = await requestGetPromotionProductsMoloco({
        id: uuidv4(),
        pageId: window.location.pathname,
        sessionId: sessionId ? encodeURIComponent(sessionId) : '',
        userId: userId || '',
        categoryFilter:
          promotion?.categoryTargetType === 'PARTIAL'
            ? promotion?.categories?.map(category => category.molocoKey)
            : undefined,
      });

      // 6개 미만이면 빈 배열 반환
      if (!result.items || result.items.length < 6) {
        return [];
      }

      // 6개 이상이고 홀수인 경우 -1개 하여 짝수로 맞춤
      if (result.items.length % 2 !== 0) {
        return result.items.slice(0, result.items.length - 1);
      }

      return result.items;
    },
    placeholderData: keepPreviousData,
  });

  const handleTracker = useCallback(() => {
    if (isTouching.current || !hasScrolled.current) {
      return;
    }
    const container = scrollContainerRef.current;
    const targetScrollLeft = container?.scrollLeft || 0;
    const targetScrollRight = targetScrollLeft + (container?.offsetWidth || 0);
    timeoutRef.current = setTimeout(() => {
      const targetItems = [];
      for (let i = 0; i < itemsRef.current.length; i++) {
        const item = itemsRef.current[i];
        const itemLeft = item.offsetLeft;
        const itemRight = itemLeft + item.offsetWidth;
        const itemWidth = item.offsetWidth;
        const itemCenter = itemLeft + itemWidth / 2;
        if (itemCenter > targetScrollLeft && itemCenter < targetScrollRight) {
          if (molocoProducts?.[i]) {
            targetItems.push(molocoProducts?.[i]);
          }
        }
      }
      sendMolocoImpTracker(
        appInfo.isNativeAppBoth,
        targetItems.map(item => item.impTrackers[0])
      );
    }, 200); // 200ms delay to consider scroll as stopped
  }, [appInfo.isNativeAppBoth, molocoProducts]);

  const productList = renderProducts({
    type: 'simple',
    products: molocoProducts || [],
    maxCount: 20,
    rs: '216',
    refs: itemsRef,
    isBrandNameKr: true,
    colorType: advertisementProductColorType,
    onClickProduct: (product, index) => {
      sendMolocoDecisionClickTracker(
        appInfo.isNativeAppBoth,
        molocoProducts?.[index]?.clickTrackers[0] || ''
      );
      sendGa4SelectItem({
        product,
        listNo: (index ?? 0) + 1,
        page: 'promotion',
        ecommercePath: '프로모션',
        area: 'promotion_ad_item',
        issueCd: promotionId?.toString(),
        issueNm: promotionName,
        affiliation: 'moloco',
      });
    },
  });

  const onIntersection = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting && !isFirstVisible && molocoProducts) {
        setIsFirstVisible(true);
        const targetItmes = molocoProducts?.slice(0, 6);
        sendMolocoImpTracker(
          appInfo.isNativeAppBoth,
          targetItmes?.map(item => item?.impTrackers[0])
        );
      }
    },
    [isFirstVisible, appInfo.isNativeAppBoth, molocoProducts]
  );

  useEffect(() => {
    const callbackFunction = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      onIntersection(entry);
    };

    let observerRefValue: Element | null = null;

    const observer = new IntersectionObserver(callbackFunction, {
      threshold: 1.0,
    });

    if (scrollContainerRef.current) {
      observer.observe(scrollContainerRef.current);
      observerRefValue = scrollContainerRef.current;
    }

    return () => {
      if (observerRefValue) observer.unobserve(observerRefValue);
    };
  }, [onIntersection, scrollContainerRef.current]);

  const renderProductList = useCallback(() => {
    if (isLoading) {
      return Array.from({ length: 12 }).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <PrdItemSkeleton key={index} bgType={advertisementSkeletonBgType} />
      ));
    }
    return Array.isArray(productList) ? productList : [productList];
  }, [productList, isLoading, advertisementSkeletonBgType]);

  const onScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
      hasScrolled.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        handleTracker();
      }, 200); // 200ms delay to consider scroll as stopped
    },
    [handleTracker]
  );

  const onTouchStart = useCallback(() => {
    isTouching.current = true;
    hasScrolled.current = false; // 터치 시작시 스크롤 상태 초기화
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (timeoutRef.current) {
      isTouching.current = false;
      clearTimeout(timeoutRef.current);
      handleTracker();
    }
    hasScrolled.current = false;
  }, [handleTracker]);

  if (!isMobile) {
    return null;
  }

  if (!isCustomAd) {
    return null;
  }

  // 6개 미만이면 노출하지 않음
  if (!isLoading && (!molocoProducts || molocoProducts.length === 0)) {
    return null;
  }

  return (
    <AdProductSection
      title={title}
      productList={renderProductList()}
      colorType={sectionColorType}
      onScroll={onScroll}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      scrollContainerRef={scrollContainerRef}
    />
  );
}

export default memo(AdvertisementModule);
